import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import ExperienceCategories from "@/components/ExperienceCategories";
import SoulprintSection from "@/components/SoulprintSection";
import LeadModal from "@/components/LeadModal";
import Footer from "@/components/Footer";

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillExp, setPrefillExp] = useState("");
  const [prefillArch, setPrefillArch] = useState("");

  const openLead = (exp = null) => {
    setPrefillExp(exp?.title || "");
    setPrefillArch(exp?.archetype || "");
    setModalOpen(true);
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="home-page">
      <Navbar onPlanJourney={() => openLead()} />
      <Hero onPlanJourney={() => openLead()} />

      {/* ── Soulprint Bridge Section ────────────────────────────────────────── */}
      <section className="py-14 md:py-20 px-6 md:px-12 bg-[#0A0A0A] text-center border-b border-white/5">
        <div className="max-w-2xl mx-auto">
          <p className="overline mb-5">Discover How You Travel</p>
          <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed mb-4">
            Soulprint identifies the motivations, emotions, and experiences that shape the journeys that feel most meaningful to you.
          </p>
          <p className="text-[#71717A] text-sm md:text-base leading-relaxed">
            In under three minutes, uncover your travel archetype and begin designing journeys that fit who you are.
          </p>
        </div>
      </section>

      <SoulprintSection />

      {/* ── Why Travelorza Exists ────────────────────────────────────────────── */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#0A0A0A] border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <p className="overline mb-6">Why Travelorza Exists</p>
          <div className="space-y-4 md:space-y-5">
            <p className="font-heading text-2xl md:text-3xl font-light text-white leading-snug">
              Most travel companies start with destinations.
            </p>
            <p className="font-heading text-2xl md:text-3xl font-light text-[#C9A84C] italic leading-snug">
              We start with people.
            </p>
            <div className="pt-2 space-y-3 max-w-2xl">
              <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed">
                Because the same destination can transform one traveler and leave another unchanged.
              </p>
              <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed">
                Travelorza was built to discover how you travel before deciding where you travel.
              </p>
            </div>
            <div className="pt-3 border-l-2 pl-5" style={{ borderColor: "#C9A84C" }}>
              <p className="font-heading text-xl md:text-2xl font-light text-white leading-relaxed">
                Every meaningful journey begins with identity.
              </p>
              <p className="font-heading text-xl md:text-2xl font-light text-[#71717A] italic leading-relaxed">
                Not itinerary.
              </p>
              {/* Mini philosophy visual — Refinement 7 */}
              <div className="mt-8 space-y-1">
                {["IDENTITY", "EXPERIENCE", "TRANSFORMATION"].map((step, i) => (
                  <div key={step}>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/55">{step}</p>
                    {i < 2 && <p className="text-[#C9A84C]/40 text-base leading-none">↓</p>}
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C9A84C]/70 hover:text-[#C9A84C] transition-colors"
                >
                  Explore Our Philosophy <ArrowRight size={11} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ExperienceCategories />

      {/* Mid-page CTA */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#111111] text-center">
        <div className="max-w-3xl mx-auto">
          <p className="overline mb-5">Begin the Conversation</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white mb-6 tracking-tight">
            Every Journey Begins with <span className="italic text-[#C9A84C]">Who You Are</span>
          </h2>
          <p className="text-[#A1A1AA] text-base mb-4 max-w-xl mx-auto leading-relaxed">
            Share your vision with our Experience Architects. We&apos;ll design something you didn&apos;t know you were searching for.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10">
            {["Designed around your personality", "Curated, not commoditized", "Every journey uniquely crafted"].map((t, i) => (
              <span key={t} className="flex items-center gap-1.5 text-[10px] text-[#71717A] uppercase tracking-[0.15em]">
                <span className="w-1 h-1 rounded-full bg-[#C9A84C]/50" />
                {t}
              </span>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/quiz"
              data-testid="mid-cta-soulprint-btn"
              className="btn-gold text-xs px-12 py-4 inline-block text-center"
            >
              Discover Your Soulprint
            </Link>
            <button
              data-testid="mid-cta-plan-btn"
              onClick={() => openLead()}
              className="btn-outline-gold text-xs px-12 py-4"
            >
              Design My Journey
            </button>
          </div>
        </div>
      </section>

      <Footer onPlanJourney={() => openLead()} />

      <LeadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        prefillExperience={prefillExp}
        prefillArchetype={prefillArch}
        source="homepage"
      />
    </div>
  );
}
